import React from "react";
import { useSelector } from "react-redux";

const PathologistReportView = () => {
  const { currentReport } = useSelector((s) => s.pathologistReports || {});
  if (!currentReport) {
    return (
      <div className="p-6">
        <div className="rounded-2xl bg-white p-6 text-sm text-gray-600 ring-1 ring-gray-200/70">
          No report loaded.
        </div>
      </div>
    );
  }

  let reportData = {};
  try {
    reportData =
      typeof currentReport.report_data === "string"
        ? JSON.parse(currentReport.report_data)
        : currentReport.report_data || {};
  } catch {
    reportData = currentReport.report_data || {};
  }

  const images = (() => {
    try {
      return typeof currentReport.images === "string"
        ? JSON.parse(currentReport.images)
        : currentReport.images || [];
    } catch {
      return [];
    }
  })();

  return (
    <div className="p-5 sm:p-6">
      <div className="space-y-4">
        <div className="rounded-2xl bg-white p-5 ring-1 ring-gray-200/70">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#235F72]/10 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#235F72]/20">
                Pathology Report
              </div>
              <h3 className="mt-2 text-lg font-semibold tracking-tight text-gray-900">
                Report #{currentReport.id}
              </h3>
              <div className="mt-1 text-sm text-gray-600">
                Patient: {currentReport.patient_name || "-"} • Card: {currentReport.card_number || "-"} • Age/Gender:{" "}
                {currentReport.patient_age || "-"}/{currentReport.patient_gender || "-"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                {String(currentReport.status || "-")}
              </span>
              {Number(currentReport.is_verified || 0) === 1 && (
                <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800 ring-1 ring-red-200">
                  Verified
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Test</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{currentReport.test_name || "-"}</div>
              <div className="mt-1 text-xs text-gray-600">Category: {currentReport.category_name || "-"}</div>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Pathologist</div>
              <div className="mt-1 text-sm font-semibold text-gray-900">{currentReport.pathologist_name || "-"}</div>
              <div className="mt-1 text-xs text-gray-600">Verified by: {currentReport.verified_by_name || currentReport.verified_by || "-"}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white ring-1 ring-gray-200/70">
          <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
            <div className="text-sm font-semibold text-gray-900">Report Data</div>
            <div className="text-xs text-gray-500">Structured payload stored with the report.</div>
          </div>
          <div className="p-4 sm:p-5">
            <pre className="max-h-96 overflow-auto rounded-2xl bg-gray-900 p-4 text-xs text-green-300 ring-1 ring-gray-200/30">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        </div>

        {images.length > 0 && (
          <div className="rounded-2xl bg-white ring-1 ring-gray-200/70">
            <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
              <div className="text-sm font-semibold text-gray-900">Images</div>
              <div className="text-xs text-gray-500">Attached report images.</div>
            </div>
            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {images.map((src, i) => (
                  <a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group overflow-hidden rounded-2xl ring-1 ring-gray-200/70"
                    title="Open image"
                  >
                    <img
                      src={src}
                      alt={`img-${i}`}
                      className="h-28 w-full object-cover transition group-hover:scale-[1.02]"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {currentReport.overall_comments && (
          <div className="rounded-2xl bg-white ring-1 ring-gray-200/70">
            <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
              <div className="text-sm font-semibold text-gray-900">Comments</div>
              <div className="text-xs text-gray-500">Overall interpretation / notes.</div>
            </div>
            <div className="p-4 text-sm text-gray-800 sm:p-5" style={{ whiteSpace: "pre-wrap" }}>
              {currentReport.overall_comments}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PathologistReportView;
